import React, { ReactElement, useCallback, useMemo } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { FormattedMessage, formatTranslation } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DefaultCheckIcon from '@mui/icons-material/Check';

import { ChecklistItemPreviewProps } from './types';
import { GraphicUploader } from 'components/common/graphicUploader';
import {
  useChecklistItemTemplateAutoOpenCheck,
} from 'components/common/graphicUploader/documentRouteSync';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { createTemplateChecklistItemDocumentsBaseUrl } from 'shared/domain/template/templateDocumentPaths';
import { ChecklistItemOnView } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { useInspectionTemplate } from 'store/inspection';
import {
  DocumentToChecklistBinder,
  IDocumentBinder,
} from 'shared/domain/document/documentBinder';
import { getDocumentScopeKey } from 'shared/domain/document/documentScope';
import { useLocation } from 'react-router-dom';
import useScrollToOnInit from 'hooks/scrollToOnInit';
import { useDisplayedFilesStoreHasFiles } from 'components/common/gallery/filesStore';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useAppNavigation } from 'contexts/navigation';
import { APP_PATHS } from 'setup/navigation/paths';
import { RichTextView } from 'components/common/RichTextEditor/RichTextView';
import { stripLeadingChecklistNumber } from 'shared/domain/checklistTree';
import { ACCORDION_UNMOUNT_ON_EXIT_SLOT_PROPS } from 'components/common/checklistTree/constants';
import { ChecklistItemAnswerModel } from 'shared/domain/template/inspectionTemplateModel';
import { MemoChecklistItemAnswersPreview } from '../ChecklistItemAnswersPreview';

import {
  accordionSx,
  accordionIndexTypographySx,
  accordionSummaryTypographySx,
  checkIconContainerSx,
  checkIconErrorContainerSx,
  checkIconSx,
  contentColumnSx,
  headerRowSx,
  templateAttachmentsWrapperSx,
} from './styles';
import { fieldLabelSx } from 'shared/styles/typography';

const EMPTY_ANSWERS: ChecklistItemAnswerModel[] = [];

function add0ToSingleDigitNumber(number: number): string {
  return number < 10 ? '0' + number : `${number}`;
}

function shouldBeExpandedBasedOnUrl(
  checklistItem: ChecklistItemOnView
): boolean {
  const pathname = window.location.pathname;
  return (
    pathname.includes(checklistItem._id) ||
    pathname.includes(`/checklist/${checklistItem._id}`)
  );
}

function ChecklistItemTemplateAttachments({
  checklistItem,
  inspectionId,
  documentScopeKey,
  internalEntityBaseUrl,
  autoOpenCheck,
  checklistDocuments,
}: {
  checklistItem: ChecklistItemOnView;
  inspectionId?: string;
  documentScopeKey: string | undefined;
  internalEntityBaseUrl: () => string;
  autoOpenCheck: ReturnType<typeof useChecklistItemTemplateAutoOpenCheck>;
  checklistDocuments:
    | ReturnType<
        typeof useInspectionTemplate
      >['inspectionTemplateDocuments']['checklistDocuments'][string]
    | undefined;
}): ReactElement | null {
  const intl = useIntl();
  const config = React.useMemo(
    () => ({
      baseUrl: createTemplateChecklistItemDocumentsBaseUrl(
        checklistItem.templateId,
        checklistItem._id
      ),
      documentScopeKey,
      previewOnly: true as const,
      internalEntityBaseUrl,
      documentBinderFactory: (): IDocumentBinder =>
        new DocumentToChecklistBinder({
          templateId: checklistItem.templateId,
          checklistId: checklistItem._id,
          ...(inspectionId ? { inspectionId } : {}),
        }),
      autoOpenCheck,
    }),
    [
      autoOpenCheck,
      checklistItem.templateId,
      checklistItem._id,
      documentScopeKey,
      inspectionId,
      internalEntityBaseUrl,
    ]
  );
  const GU = useGraphicUploader(checklistDocuments, config);

  const hasTemplateFiles = useDisplayedFilesStoreHasFiles(GU.filesStore);
  if (!hasTemplateFiles) {
    return null;
  }

  return (
    <Box sx={templateAttachmentsWrapperSx}>
      <Typography component='p' sx={fieldLabelSx}>
        {formatTranslation(
          intl,
          InspectionTranslationKeys.sectionPreviewFieldLabelTemplateAttachments
        )}
      </Typography>
      <GraphicUploader {...GU} />
    </Box>
  );
}

function ChecklistItemPreview(
  props: ChecklistItemPreviewProps
): ReactElement {
  const {
    checklistItem,
    index,
    displayNumber,
    showStatusIcon = true,
    children,
    CheckIcon,
    error,
    canChangeUrl,
    inspectionId,
    internalEntityBaseUrlOverride,
    hideAnswersPreview,
    answerProgress,
  } = props;
  const indexPlusOne = index + 1;
  const intl = useIntl();
  const location = useLocation();
  const { inspectionTemplateDocuments } = useInspectionTemplate();
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);

  const internalEntityBaseUrl = useMemo(() => {
    if (internalEntityBaseUrlOverride) {
      return internalEntityBaseUrlOverride;
    }
    return (): string =>
      APP_PATHS.inspectionTemplate.checklistItem(
        projectId,
        checklistItem.templateId,
        checklistItem._id
      );
  }, [
    internalEntityBaseUrlOverride,
    projectId,
    checklistItem.templateId,
    checklistItem._id,
  ]);

  const documentScopeKey = useMemo(
    () =>
      getDocumentScopeKey({
        ...(inspectionId ? { inspectionId } : {}),
        templateId: checklistItem.templateId,
      }),
    [inspectionId, checklistItem.templateId]
  );

  const autoOpenCheck = useChecklistItemTemplateAutoOpenCheck(
    inspectionId,
    checklistItem.templateId,
    checklistItem._id
  );

  const isDeepLinked = shouldBeExpandedBasedOnUrl(checklistItem);
  const [internalExpanded, setInternalExpanded] = React.useState<boolean>(
    isDeepLinked
  );
  const isControlled = props.expanded !== undefined;
  const expanded = isControlled ? !!props.expanded : internalExpanded;

  const { goToInspectionTemplateChecklistItem } = useAppNavigation();

  const { onExpandedChange } = props;

  const handleExpanded = useCallback(
    (_, isExpanded: boolean) => {
      if (isExpanded && canChangeUrl) {
        goToInspectionTemplateChecklistItem(
          projectId,
          checklistItem.templateId,
          checklistItem._id,
          { replace: true }
        );
      }
      if (!isControlled) {
        setInternalExpanded(isExpanded);
      }
      onExpandedChange?.(isExpanded);
    },
    [
      canChangeUrl,
      goToInspectionTemplateChecklistItem,
      checklistItem.templateId,
      checklistItem._id,
      projectId,
      isControlled,
      onExpandedChange,
    ]
  );
  const expandIcon = React.useMemo(() => <ExpandMoreIcon />, []);
  const summaryLabel = useMemo(() => {
    if (!displayNumber) {
      return checklistItem.summary;
    }

    return stripLeadingChecklistNumber(checklistItem.summary);
  }, [displayNumber, checklistItem.summary]);

  const accordionId = checklistItem._id;
  const isReturnTarget =
    (location.state as { checklistItemId?: string } | null)?.checklistItemId ===
    accordionId;
  const shouldScroll = Boolean(expanded && (isReturnTarget || isDeepLinked));
  useScrollToOnInit(
    shouldScroll,
    `#${CSS.escape(accordionId)}`,
    [expanded],
    200
  );

  const descriptionBlock = checklistItem.norm.description ? (
    <Box>
      <Typography component='p' sx={fieldLabelSx}>
        {formatTranslation(
          intl,
          InspectionTranslationKeys.itemPreviewFieldLabelDescription
        )}
      </Typography>
      <RichTextView content={checklistItem.norm.description} />
    </Box>
  ) : null;

  const answersPreviewBlock = !hideAnswersPreview ? (
    <MemoChecklistItemAnswersPreview
      answers={checklistItem.answers ?? EMPTY_ANSWERS}
    />
  ) : null;

  return (
    <Accordion
      id={accordionId}
      expanded={expanded}
      onChange={handleExpanded}
      slotProps={ACCORDION_UNMOUNT_ON_EXIT_SLOT_PROPS}
      sx={accordionSx}
    >
      <AccordionSummary
        id={`checklistItemPreview${index}`}
        expandIcon={expandIcon}
        aria-label='Expand'
        data-qa='checklist-item-preview-expand'
      >
        <Stack direction='row' alignItems='center' sx={headerRowSx}>
          <Typography
            noWrap={false}
            fontWeight={expanded ? 700 : 400}
            sx={accordionIndexTypographySx(Boolean(error))}
          >
            {displayNumber || add0ToSingleDigitNumber(indexPlusOne)}
          </Typography>
          <Typography
            noWrap={false}
            fontWeight={expanded ? 700 : 400}
            sx={accordionSummaryTypographySx(Boolean(error))}
          >
            {summaryLabel}
          </Typography>
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {answerProgress?.isNotApplicable ? (
            <Typography
              variant='body2'
              color='text.secondary'
              data-qa='checklist-item-summary-not-applicable'
              sx={{ whiteSpace: 'nowrap' }}
            >
              {formatTranslation(intl, InspectionTranslationKeys.notApplicable)}
            </Typography>
          ) : null}
          {answerProgress &&
          answerProgress.total > 0 &&
          !answerProgress.isNotApplicable ? (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ whiteSpace: 'nowrap' }}
            >
              <FormattedMessage id={InspectionTranslationKeys.protocolItemAnsweredSummary}
                values={{
                  answered: answerProgress.answered,
                  total: answerProgress.total,
                }}
              />
            </Typography>
          ) : null}
          {showStatusIcon && !answerProgress?.isNotApplicable ? (
            <Box sx={error ? checkIconErrorContainerSx : checkIconContainerSx}>
              {CheckIcon ? (
                <CheckIcon sx={checkIconSx} />
              ) : (
                <DefaultCheckIcon sx={checkIconSx} />
              )}
            </Box>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {/* `unmountOnExit` drops this after the collapse animation finishes. */}
        <Stack direction='column' sx={contentColumnSx} spacing={1.5}>
          {descriptionBlock}
          <ChecklistItemTemplateAttachments
            checklistItem={checklistItem}
            inspectionId={inspectionId}
            documentScopeKey={documentScopeKey}
            internalEntityBaseUrl={internalEntityBaseUrl}
            autoOpenCheck={autoOpenCheck}
            checklistDocuments={
              inspectionTemplateDocuments.checklistDocuments[checklistItem._id]
            }
          />
          {answersPreviewBlock}
          {children}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export const MemoChecklistItemPreview = React.memo(ChecklistItemPreview);
